import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import {
	FaceFrownIcon,
	MagnifyingGlassIcon,
	GlobeAmericasIcon,
	InformationCircleIcon,
} from '@heroicons/react/24/outline';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

import { searchGlobally } from '../api';

import {
	capitalizeFirstLetter,
	classNames,
} from '../components/helpers/functions';

import { AvatarGenerated } from '../components/Avatar';

const MODAL_NAME = 'globalSearch';

const GlobalSearch = () => {
	const navigate = useNavigate();
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openAsset } = useModal('viewAsset');

	const [query, setQuery] = useState('');

	const {
		value: results,
		execute: search,
		resetValue,
	} = useAsync(searchGlobally, {
		immediate: false,
	});

	const items = results || [];
	useEffect(() => {
		if (query) {
			const delayDebounceFn = setTimeout(
				() => {
					search({ query, limit: 5 });
				},
				query ? 600 : 0,
			);

			return () => clearTimeout(delayDebounceFn);
		} else {
			resetValue();
		}
	}, [query]);

	const getPath = (category, id) =>
		({
			assets: '/assets/:id',
			locations: '/assets/all?location=:id',
		})[category].replace(':id', id);

	return (
		<Transition.Root
			show={!!open}
			as={Fragment}
			afterLeave={() => setQuery('')}
			appear
		>
			<Dialog as="div" className="relative z-50" onClose={setClose}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-gray-500/25 transition-opacity" />
				</Transition.Child>

				<div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<Dialog.Panel className="mx-auto max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
							<Combobox>
								<div className="relative">
									<MagnifyingGlassIcon
										className="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400"
										aria-hidden="true"
									/>
									<Combobox.Input
										className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-800 placeholder-gray-400 focus:ring-0 focus:outline-none sm:text-sm"
										placeholder="Search..."
										onChange={(event) => setQuery(event.target.value)}
									/>
								</div>

								{query === '' && (
									<div className="border-t border-gray-100 py-14 px-6 text-center text-sm sm:px-14">
										<GlobeAmericasIcon
											className="mx-auto h-6 w-6 text-gray-400"
											aria-hidden="true"
										/>
										<p className="mt-4 font-semibold text-gray-900">
											Search for assets, locations, etc
										</p>
										<p className="mt-2 text-gray-500">
											Start typing away and we'll do the rest
										</p>
									</div>
								)}

								{Object.keys(items).filter((k) => items[k].length).length >
									0 && (
									<Combobox.Options
										static
										className="max-h-120 scroll-pt-11 scroll-pb-2 space-y-2 overflow-y-auto pb-2"
									>
										{Object.entries(items).map(([category, items]) =>
											items.length ? (
												<li key={category}>
													<h2 className="bg-gray-100 py-2.5 px-4 text-xs font-semibold text-gray-900">
														{capitalizeFirstLetter(category)}
													</h2>
													<ul className="mt-2 text-sm text-gray-800">
														{items.map((item) => (
															<Combobox.Option
																key={item.id}
																value={item}
																className={({ active }) =>
																	classNames(
																		'cursor-pointer select-none px-4 py-2',
																		active && 'bg-indigo-50',
																	)
																}
																onClick={() => {
																	setClose({
																		onClose: () =>
																			navigate(getPath(category, item.id)),
																	});
																}}
															>
																{
																	{
																		assets: (
																			<>
																				<div className="flex items-center justify-between">
																					<div className="flex items-center">
																						<AvatarGenerated
																							id={item?.id}
																							type="asset"
																							size="sm"
																						/>
																						<div className=" ml-6">
																							<div className="flex items-center">
																								{`${
																									item?.manufacturerName ||
																									item?.manufacturer?.name
																								} ${
																									item?.modelName ||
																									item?.model?.name
																								}`}
																							</div>
																							<div className="text-xs text-gray-400 font-light">
																								{`S/N: ${item?.serialNumber}`}{' '}
																								{item?.location?.name
																									? ` in ${item?.location?.name}`
																									: ''}
																							</div>
																						</div>
																					</div>
																					<div
																						className="p-5 pr-3"
																						onClick={(e) => {
																							e.preventDefault();
																							openAsset({
																								id: item?.id,
																								showView: true,
																								onClose: () => setClose(),
																							});
																						}}
																					>
																						<InformationCircleIcon className="text-gray-600 w-5 h-5" />
																					</div>
																				</div>
																			</>
																		), //getAssetName(item),
																		locations: item?.name,
																		contacts: '',
																		documents: '',
																	}[category]
																}
															</Combobox.Option>
														))}
													</ul>
												</li>
											) : null,
										)}
									</Combobox.Options>
								)}

								{query !== '' &&
									Object.keys(items).filter((k) => items[k].length).length ===
										0 && (
										<div className="border-t border-gray-100 py-14 px-6 text-center text-sm sm:px-14">
											<FaceFrownIcon
												className="mx-auto h-6 w-6 text-gray-400"
												aria-hidden="true"
											/>
											<p className="mt-4 font-semibold text-gray-900">
												No results found
											</p>
											<p className="mt-2 text-gray-500">
												We couldn’t find anything with that term. Please try
												again.
											</p>
										</div>
									)}
							</Combobox>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition.Root>
	);
};

export default GlobalSearch;
